import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listSummaries, listPendingLeaves, getComplianceReminders, getCurrentCycle, listEvaluations, listRecommendations } from '../lib/db.js';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';
import { useAuth } from '../lib/auth.jsx';
import { thaiDate } from '../lib/format.js';

const TODAY = new Date().toLocaleDateString('th-TH', { weekday: 'short', day: 'numeric', month: 'short' });
const COMPLIANCE_WINDOW_DAYS = 120;

export function Home() {
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const [rows, setRows] = useState(null);
  const [pendingLeave, setPendingLeave] = useState(0);
  const [compliance, setCompliance] = useState(null);
  const [evalPct, setEvalPct] = useState(null);
  const [pendingReco, setPendingReco] = useState(0);
  const [err, setErr] = useState('');

  useEffect(() => {
    listSummaries().then(setRows).catch((e) => { setErr(e.message); setRows([]); });
    listPendingLeaves().then((l) => setPendingLeave(l.length)).catch(() => {});
    // ข้อเสนอเงินเดือน/โบนัส = แอดมินเท่านั้น
    if (isAdmin) listRecommendations().then((rs) => setPendingReco((rs || []).filter((r) => (r.manager_decision || r.status) === 'pending').length)).catch(() => {});
    getCurrentCycle().then((c) => c && listEvaluations(c.id)).then((evs) => {
      if (!evs || !evs.length) return;
      const done = evs.filter((e) => e.status === 'submitted' || e.status === 'acknowledged').length;
      setEvalPct(Math.round((done / evs.length) * 100));
    }).catch(() => {});
    getComplianceReminders().then((rs) => {
      // โชว์อันที่ใกล้กำหนดที่สุดภายในช่วงเตือน
      const now = Date.now();
      const due = (rs || [])
        .map((r) => ({ ...r, days: Math.round((new Date(r.due_date) - now) / 86400000) }))
        .filter((r) => r.days <= COMPLIANCE_WINDOW_DAYS)
        .sort((a, b) => a.days - b.days);
      setCompliance(due[0] || null);
    }).catch(() => {});
  }, [isAdmin]);

  if (err) return <Shell><p className="err">{err}</p></Shell>;
  if (!rows) return <Shell><Loading /></Shell>;

  const headcount = rows.length;
  const totalWarnings = sum(rows, 'active_warnings');
  const totalLate = sum(rows, 'late_count_ytd');
  const totalAbsent = sum(rows, 'absent_count_ytd');
  const totalCommends = sum(rows, 'commendation_count');
  const totalPoints = sum(rows, 'reward_points');
  const flagged = rows
    .filter((r) => r.active_warnings > 0)
    .sort((a, b) => b.active_warnings - a.active_warnings);

  // เรื่องที่ต้องดำเนินการ = ใบเตือนใกล้/มีผล + คำขอลารออนุมัติ
  const todo = flagged.length + pendingLeave;

  return (
    <Shell>
      <div className="title-row">
        <div>
          <div className="eyebrow">วันนี้ · {TODAY}</div>
          <h1 className="page">ภาพรวม</h1>
        </div>
        <span className="glyph">◴</span>
      </div>

      {compliance && (
        <div className="compliance-banner">
          <span className="cb-icon">▤</span>
          <div>
            <div className="cb-title">{compliance.type === 'kor11' ? 'ใกล้กำหนด คร.11' : 'งานที่ต้องเตรียม'}</div>
            <div className="cb-sub">{compliance.title} · ภายใน {thaiDate(compliance.due_date)}
              {compliance.days >= 0 ? ` (อีก ${compliance.days} วัน)` : ' (เลยกำหนด)'}</div>
          </div>
        </div>
      )}

      <div className="hero">
        <div className="num">{todo}</div>
        <div className="sub">เรื่องที่ต้องดำเนินการ</div>
      </div>

      <div className="rail">
        <Tick lab="พนักงาน" dot="○" val={headcount} />
        <Tick lab="รออนุมัติลา" dot="🗓" val={pendingLeave} />
        <Tick lab="ชมเชย" dot="★" val={totalCommends} />
        {evalPct != null && <Tick lab="ประเมิน" dot="◷" val={`${evalPct}%`} />}
        <Tick lab="ใบเตือน" dot="⚠︎" val={totalWarnings} />
        <Tick lab="มาสาย" dot="◔" val={totalLate} />
        <Tick lab="ขาด" dot="○" val={totalAbsent} />
      </div>

      <hr className="hr" />

      <div className="grid">
        <Stat pip="warn" k="ใบเตือนมีผล" v={totalWarnings} unit="ฉบับ" />
        <Stat pip="ok" k="ชมเชยรวม" v={totalCommends} unit="ครั้ง" />
        <Stat pip="ok" k="แต้มรางวัลรวม" v={totalPoints} unit="แต้ม" />
        <Stat pip="pend" k="มาสาย (ปีนี้)" v={totalLate} unit="ครั้ง" />
      </div>

      <div className="sec-label">ต้องสนใจ</div>
      {pendingLeave > 0 && (
        <Link className="row" to="/leave">
          <div className="ic">🗓</div>
          <div className="main">
            <div className="l1">คำขอลารออนุมัติ</div>
            <div className="l2">กดเพื่ออนุมัติ/ไม่อนุมัติ</div>
          </div>
          <span className="tag pend">{pendingLeave} คำขอ</span>
        </Link>
      )}
      {evalPct != null && evalPct < 100 && (
        <Link className="row" to="/evaluations">
          <div className="ic">◷</div>
          <div className="main">
            <div className="l1">รอบประเมินยังไม่ครบ</div>
            <div className="l2">ประเมินเสร็จ {evalPct}%</div>
          </div>
          <span className="tag pend">{evalPct}%</span>
        </Link>
      )}
      {flagged.length === 0 && pendingLeave === 0 && <p className="muted">ไม่มีเรื่องค้าง 👍</p>}
      {flagged.map((r) => (
        <Link className="row" key={r.id} to={`/employees/${r.id}`}>
          <div className="ic">⚠︎</div>
          <div className="main">
            <div className="l1">{r.nickname || r.first_name}</div>
            <div className="l2">{r.branch || '—'}</div>
          </div>
          <span className="tag warn">{r.active_warnings} ฉบับ</span>
        </Link>
      ))}

      {isAdmin && (
        <>
          <div className="sec-label">เครื่องมือแอดมิน</div>
          <Link className="row" to="/recommendations">
            <div className="ic">✦</div>
            <div className="main">
              <div className="l1">ข้อเสนอขึ้นเงิน/เลื่อน/โบนัส</div>
              <div className="l2">ระบบแนะนำ — แอดมินตัดสิน</div>
            </div>
            {pendingReco > 0
              ? <span className="tag pend">{pendingReco} รอ</span>
              : <span className="tag ok">ดู ›</span>}
          </Link>
          <Link className="row" to="/severance">
            <div className="ic">🧮</div>
            <div className="main">
              <div className="l1">คำนวณค่าชดเชย (เลิกจ้าง)</div>
              <div className="l2">ตามอายุงาน + ตรวจ ladder + เช็กลิสต์</div>
            </div>
            <span className="tag ok">ดู ›</span>
          </Link>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">{children}</section>
      <BottomNav />
    </div>
  );
}
function Loading() {
  return <div className="center"><div className="spinner" /><span className="muted">กำลังโหลด…</span></div>;
}
function Tick({ lab, dot, val }) {
  return <div className="tick"><span className="lab">{lab}</span><span className="dot">{dot}</span><span className="val">{val}</span></div>;
}
function Stat({ pip, k, v, unit }) {
  return (
    <div className="stat">
      <div className="k">{pip && <span className={`pip ${pip}`} />}{k}</div>
      <div className="v">{v} <small>{unit}</small></div>
    </div>
  );
}
function sum(rows, key) { return rows.reduce((a, r) => a + (r[key] || 0), 0); }
